import React, { useState, useEffect, useMemo, useContext } from "react";
import * as S from "./PaginaClienteStyle";
import * as Modal from "./ModalConsultor/ModalConsultorStyle";
import Loading from "../Loader";
import ModalConsultor from "./ModalConsultor/ModalConsultor";
import ModalReinvestimento from "./ModalReinvestimento/ModalReinvestimento";
import {
  FaEdit,
  FaWhatsapp,
  FaSignInAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
  FaEye,
  FaCrown,
  FaUser,
} from "react-icons/fa";
import { useMessage } from "../../Context/MessageContext";
import adminAxiosInstance from "../../Services/api/adminAxiosInstance";
import { authAdminService } from "../../Services/authService";
import { auditService } from "../../Services/auditService";
import { AuthContext } from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { clientAdminService } from "../../Services/clientAdminService";

const formatClientCpfCnpj = (cpfCnpj = "") => {
  const cleaned = cpfCnpj.replace(/\D/g, "");
  if (cleaned.length === 11)
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (cleaned.length === 14)
    return cleaned.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  return cpfCnpj;
};

const EditCpfModal = ({ onClose, onSave, currentCpf }) => {
  const [newCpf, setNewCpf] = useState("");
  return (
    <Modal.ModalOverlay>
      <Modal.ModalContainer size="small">
        <Modal.ModalHeader>
          <h3>Alterar CPF/CNPJ</h3>
          <Modal.CloseButton onClick={onClose}>&times;</Modal.CloseButton>
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <div style={{ color: "#fff" }}>
            CPF/CNPJ Atual: {formatClientCpfCnpj(currentCpf)}
          </div>
          <input
            value={newCpf}
            onChange={(e) => setNewCpf(e.target.value)}
            placeholder="Digite o novo CPF/CNPJ"
            style={{
              width: "100%",
              height: "45px",
              background: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              padding: "0 15px",
              boxSizing: "border-box",
              borderRadius: "6px",
              fontSize: 16,
              outline: "none",
              marginTop: "15px",
            }}
          />
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Modal.CancelButton onClick={onClose}>Cancelar</Modal.CancelButton>
          <Modal.SaveButton onClick={() => onSave(newCpf)} disabled={!newCpf}>
            Salvar
          </Modal.SaveButton>
        </Modal.ModalFooter>
      </Modal.ModalContainer>
    </Modal.ModalOverlay>
  );
};

export default function PaginaCliente({
  fetchClients,
  clienteData,
  handleClose,
}) {
  const [editedData, setEditedData] = useState(clienteData);
  const [isLoading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    consultor: false,
    reinvestimento: false,
    cpf: false,
  });
  const [usernameStatus, setUsernameStatus] = useState({
    status: "idle",
    message: "",
  });
  const [isPhoneVisible, setIsPhoneVisible] = useState(false);
  const { showMessage } = useMessage();
  const { authState } = useContext(AuthContext);
  const navigate = useNavigate();

  const hasChanges = useMemo(
    () => JSON.stringify(editedData) !== JSON.stringify(clienteData),
    [editedData, clienteData]
  );

  useEffect(() => {
    setEditedData(clienteData);
    setUsernameStatus({ status: "idle", message: "" });
    setIsPhoneVisible(false);
  }, [clienteData]);

  if (!clienteData) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const [field, subfield] = name.split(".");
    const finalValue = type === "checkbox" ? checked : value;
    if (name === "username" && value !== clienteData.username) {
      setUsernameStatus({ status: "idle", message: "" });
    }
    if (subfield) {
      setEditedData((prev) => ({
        ...prev,
        [field]: { ...(prev[field] || {}), [subfield]: finalValue },
      }));
    } else {
      setEditedData((prev) => ({ ...prev, [name]: finalValue }));
    }
  };

  const checkUsername = async () => {
    if (!editedData.username || editedData.username === clienteData.username)
      return;
    setUsernameStatus({ status: "checking", message: "Verificando..." });
    try {
      const response = await adminAxiosInstance.get(
        `client/verify-username?username=${editedData.username}`
      );
      setUsernameStatus(
        response.data.isAvailable
          ? { status: "available", message: "Disponível" }
          : { status: "taken", message: "Indisponível" }
      );
    } catch (error) {
      setUsernameStatus({ status: "error", message: "Erro ao verificar" });
    }
  };

  const handleSave = async () => {
    if (
      editedData.username !== clienteData.username &&
      usernameStatus.status !== "available"
    ) {
      showMessage(
        "O nome de usuário escolhido não está disponível ou não foi verificado.",
        "warning"
      );
      return;
    }
    const patchOperations = [];
    const checkAndPush = (fieldName, editedValue, originalValue) => {
      if (JSON.stringify(editedValue) !== JSON.stringify(originalValue)) {
        patchOperations.push({ fieldName, fieldNewValue: editedValue });
      }
    };
    checkAndPush("name", editedData.name, clienteData.name);
    checkAndPush(
      "phoneNumber",
      editedData.phoneNumber,
      clienteData.phoneNumber
    );
    checkAndPush("jobTitle", editedData.jobTitle, clienteData.jobTitle);
    checkAndPush("username", editedData.username, clienteData.username);
    checkAndPush("reinvest", editedData.reinvest, clienteData.reinvest);
    checkAndPush(
      "consultorId",
      editedData.consultorId,
      clienteData.consultorId
    );
    const addressFields = [
      "street",
      "number",
      "complement",
      "neighborhood",
      "city",
      "zipcode",
      "state",
    ];
    addressFields.forEach((field) =>
      checkAndPush(
        `address.${field}`,
        editedData.address?.[field],
        clienteData.address?.[field]
      )
    );

    if (patchOperations.length === 0) {
      showMessage("Nenhuma alteração detectada.", "info");
      return;
    }
    setLoading(true);
    try {
      const response = await adminAxiosInstance.patch(
        `client/by-token`,
        patchOperations,
        { headers: { "X-Client-ID": clienteData.id } }
      );
      if (response.status === 200) {
        showMessage("Dados alterados com sucesso", "success");
        fetchClients();
        handleClose();
      }
    } catch (error) {
      showMessage(
        `Houve um erro: ${error.response?.data?.message || error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePostEditedCpf = async (newCpfValue) => {
    if (!newCpfValue || newCpfValue.trim() === "")
      return showMessage(`O valor do novo CPF não pode ser vazio.`, "warning");
    setLoading(true);
    try {
      const response = await adminAxiosInstance.patch(
        `client/cpf-cnpj`,
        { cpfCnpj: newCpfValue },
        { headers: { "X-Client-ID": clienteData.id } }
      );
      if (response.status === 204) {
        showMessage(
          `CPF alterado com sucesso. A lista será atualizada.`,
          "success"
        );
        closeModal("cpf");
        fetchClients();
        handleClose();
      }
    } catch (error) {
      showMessage(
        "Erro ao alterar o CPF: " +
          (error.response?.data?.message || error.message),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVip = async () => {
    const newStatus = !editedData.isVipClient;
    setLoading(true);
    try {
      await clientAdminService.updateVipStatus(clienteData.id, newStatus);
      showMessage(
        `Cliente ${
          newStatus ? "promovido a VIP" : "alterado para Normal"
        } com sucesso!`,
        "success"
      );
      setEditedData((prev) => ({ ...prev, isVipClient: newStatus }));
      fetchClients();
    } catch (error) {
      showMessage("Erro ao atualizar status VIP.", "error");
    } finally {
      setLoading(false);
    }
  };

  const clientPageClick = async () => {
    setLoading(true);
    try {
      const tokens = await authAdminService.impersonateClient(clienteData.id);
      await auditService.registerAdminClientLogin(
        clienteData.id,
        clienteData.name
      );
      const { accessToken, refreshToken } = tokens;
      const clientAppUrl = `https://areadocliente.goldenbrasil.com.br/token-login?accessToken=${accessToken}&refreshToken=${refreshToken}`;
      // const clientAppUrl = `http://localhost:3000/token-login?accessToken=${accessToken}&refreshToken=${refreshToken}`;
      window.open(clientAppUrl, "_blank");
    } catch (error) {
      showMessage("Não foi possível acessar a área do cliente.", "error");
      console.error("Erro de personificação:", error);
    } finally {
      setLoading(false);
    }
  };

  const verifyIfHasPermission = () => {
    return authState &&
      authState.editPermissions &&
      authState.editPermissions.client
      ? true
      : false;
  };

  const handleViewPhoneClick = async () => {
    if (!isPhoneVisible) {
      setIsPhoneVisible(true);
      await auditService.registerPhoneView(clienteData.id, clienteData.name);
    }
  };

  const whatsappClick = async () => {
    if (editedData.phoneNumber)
      window.open(
        `https://wa.me/55${editedData.phoneNumber.replace(/\D/g, "")}`,
        "_blank"
      );
    else showMessage(`Número de telefone não informado.`, "warning");
    await auditService.registerPhoneView(clienteData.id, clienteData.name);
  };

  const closeModal = (modalName, hasChanged) => {
    setModal((prev) => ({ ...prev, [modalName]: false }));
    if (hasChanged) fetchClients();
  };

  const handleViewReceipts = () => {
    navigate(`/recibos?clientSearch=${clienteData.nameSearch}`);
  };

  return (
    <>
      <Loading load={isLoading} />
      {modal.consultor && (
        <ModalConsultor
          client={editedData}
          setClient={setEditedData}
          onClose={(c) => closeModal("consultor", c)}
        />
      )}
      {modal.reinvestimento && (
        <ModalReinvestimento
          client={editedData}
          setClient={setEditedData}
          onClose={(c) => closeModal("reinvestimento", c)}
        />
      )}
      {modal.cpf && (
        <EditCpfModal
          currentCpf={clienteData.cpfCnpj}
          onClose={() => closeModal("cpf")}
          onSave={handlePostEditedCpf}
        />
      )}

      <S.PaginaClienteContainer>
        <S.Header>
          <h2>Detalhes do Cliente</h2>
          <Modal.BaseButton
            onClick={handleClose}
            style={{ background: "transparent" }}
          >
            <FaTimes size={22} />
          </Modal.BaseButton>
        </S.Header>
        <S.MainContent>
          <S.InfoGrid>
            <S.InfoBox style={{ gridColumn: "1 / -1" }}>
              <label>Nome</label>
              <input
                name="name"
                value={editedData.name || ""}
                disabled={!verifyIfHasPermission()}
                onChange={handleInputChange}
              />
            </S.InfoBox>

            <S.InfoBox>
              <label>CPF/CNPJ</label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span className="value">
                  {formatClientCpfCnpj(editedData.cpfCnpj)}
                </span>
                {verifyIfHasPermission() && (
                  <S.EditButton
                    onClick={() => setModal((prev) => ({ ...prev, cpf: true }))}
                  >
                    <FaEdit />
                  </S.EditButton>
                )}
              </div>
            </S.InfoBox>

            <S.InfoBox>
              <label>Username</label>
              <S.UsernameWrapper>
                <input
                  name="username"
                  value={editedData.username || ""}
                  onChange={handleInputChange}
                  className="username-input"
                  disabled={!verifyIfHasPermission()}
                />
                {editedData.username &&
                  editedData.username !== clienteData.username &&
                  usernameStatus.status === "idle" && (
                    <S.VerifyButton onClick={checkUsername}>
                      Verificar
                    </S.VerifyButton>
                  )}
                {usernameStatus.status !== "idle" && (
                  <S.StatusIndicator status={usernameStatus.status}>
                    {usernameStatus.status === "checking" && (
                      <FaSpinner className="spinner" />
                    )}
                    {usernameStatus.status === "available" && <FaCheckCircle />}
                    {usernameStatus.status === "taken" && (
                      <FaExclamationCircle />
                    )}
                    <span>{usernameStatus.message}</span>
                  </S.StatusIndicator>
                )}
              </S.UsernameWrapper>
            </S.InfoBox>

            <S.InfoBox>
              <label>Email</label>
              <input name="email" value={editedData.email || ""} readOnly />
            </S.InfoBox>

            <S.InfoBox>
              <label>Telefone</label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                {isPhoneVisible ? (
                  <input
                    name="phoneNumber"
                    value={editedData.phoneNumber || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        color: "#aaa",
                        userSelect: "none",
                        fontStyle: "italic",
                      }}
                    >
                      Oculto
                    </span>
                    <S.EditButton onClick={handleViewPhoneClick}>
                      <FaEye /> Visualizar
                    </S.EditButton>
                  </div>
                )}
                <S.EditButton
                  onClick={whatsappClick}
                  title="Contatar via WhatsApp"
                >
                  <FaWhatsapp />
                </S.EditButton>
              </div>
            </S.InfoBox>

            <S.InfoBox>
              <label>Profissão</label>
              <input
                name="jobTitle"
                value={editedData.jobTitle || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>Endereço</label>
              <input
                name="address.street"
                value={editedData.address?.street || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>Número</label>
              <input
                name="address.number"
                value={editedData.address?.number || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>Bairro</label>
              <input
                name="address.neighborhood"
                value={editedData.address?.neighborhood || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>Cidade</label>
              <input
                name="address.city"
                value={editedData.address?.city || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>CEP</label>
              <input
                name="address.zipcode"
                value={editedData.address?.zipcode || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>
            <S.InfoBox>
              <label>Estado</label>
              <input
                name="address.state"
                value={editedData.address?.state || ""}
                onChange={handleInputChange}
                disabled={!verifyIfHasPermission()}
              />
            </S.InfoBox>

            <S.ActionBox>
              <div>
                <label>Tipo de Cliente</label>
                <span
                  className="value"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {editedData.isVipClient ? (
                    <>
                      <FaCrown style={{ color: "#E6AA3A" }} />
                      Cliente VIP
                    </>
                  ) : (
                    <>
                      <FaUser style={{ opacity: 0.5 }} />
                      Cliente Normal
                    </>
                  )}
                </span>
              </div>
              {verifyIfHasPermission() && (
                <S.VipToggleButton
                  isVip={editedData.isVipClient}
                  onClick={handleToggleVip}
                >
                  {editedData.isVipClient ? "Rebaixar" : "Tornar VIP"}
                </S.VipToggleButton>
              )}
            </S.ActionBox>

            <S.ActionBox>
              <div>
                <label>Consultor</label>
                <span className="value">
                  {editedData.consultorName || "N/A"}
                </span>
              </div>
              {verifyIfHasPermission() && (
                <S.EditButton
                  onClick={() =>
                    setModal((prev) => ({ ...prev, consultor: true }))
                  }
                >
                  <FaEdit />
                </S.EditButton>
              )}
            </S.ActionBox>
            <S.ActionBox>
              <div>
                <label>Reinvestimento</label>
                <span className="value">
                  {editedData.reinvest ? "Ativado" : "Desativado"}
                </span>
              </div>
              {verifyIfHasPermission() && (
                <S.EditButton
                  onClick={() =>
                    setModal((prev) => ({ ...prev, reinvestimento: true }))
                  }
                >
                  <FaEdit />
                </S.EditButton>
              )}
            </S.ActionBox>
            <S.ActionBox
              style={{ cursor: "pointer" }}
              onClick={handleViewReceipts}
            >
              <div>
                <label>Recibos</label>
                <span className="value">Ver Recibos do Cliente</span>
              </div>
            </S.ActionBox>
          </S.InfoGrid>
        </S.MainContent>
        <S.Footer>
          <Modal.BaseButton
            onClick={clientPageClick}
            style={{ background: "rgba(255,255,255,0.1)" }}
          >
            <FaSignInAlt /> Acessar Área do Cliente
          </Modal.BaseButton>
          <Modal.SaveButton
            onClick={handleSave}
            disabled={
              !hasChanges ||
              usernameStatus.status === "checking" ||
              (editedData.username !== clienteData.username &&
                usernameStatus.status !== "available")
            }
          >
            Salvar Alterações
          </Modal.SaveButton>
        </S.Footer>
      </S.PaginaClienteContainer>
    </>
  );
}
